import { useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useAppSelector } from '../../app/hooks'
import { fetchSceneById } from '../../features/scene/sceneThunk'
import { generateActions } from '../../shared/utils/actions.utils'
import { clearScene } from '../../features/scene/sceneSlice'
import { getFilePath } from '../../shared/utils'
import { parentMeta } from '../content/ContentBlock/type'
import { useHistory } from 'react-router-dom'

interface UseSceneProps {
  sceneId: string
}

const usePOI = (props: UseSceneProps) => {
  const { sceneId } = props
  const dispatch = useDispatch()
  const history = useHistory()

  // =======================
  // Selectors
  // =======================
  const { data: zoneData } = useAppSelector((state) => state.zone)
  const {
    data: scene,
    loading: sceneLoading,
    error: sceneError,
  } = useAppSelector((state) => state.scene)

  // =======================
  // Effects
  // =======================
  useEffect(() => {
    if (sceneId) {
      dispatch(fetchSceneById(sceneId))
    }
    return () => {
      // clear scene on unmount
      dispatch(clearScene())
    }
  }, [dispatch, sceneId])

  // check if scene has a redirect
  useEffect(() => {
    const redirect = scene?.redirect
    if (!redirect?.target) return
    const { target: targetScene, timeout, showOnce } = redirect
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    // compile path
    const segments = history.location.pathname.split('/').filter(Boolean)
    // remove any trailing and leading slashes
    const normalizedTarget = targetScene.replace(/^\/+|\/+$/g, '')
    segments[segments.length - 1] = normalizedTarget
    const targetPath = '/' + segments.join('/')
    if (timeout && timeout > 0) {
      timeoutId = setTimeout(() => {
        history.replace(targetPath)
      }, timeout)
    } else {
      history.replace(targetPath)
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [scene])

  // =======================
  // Props
  // =======================
  const loading = sceneLoading || !scene?.id || !zoneData?.id
  const actions = generateActions({ sceneMap: scene?.scenes ?? {} })
  const itemsFound = Object.keys({ ...scene?.items }).length ? true : false
  const content = useMemo(() => {
    if (!zoneData?.id || !scene?.id) return null
    return {
      filePath: getFilePath({
        filePath: scene?.filePath,
        zoneId: zoneData.id,
        sceneId: scene.id,
      }),
      parentMeta: {
        id: scene?.id,
        title: scene?.title,
      } as parentMeta,
    }
  }, [zoneData?.id, scene?.id])

  return {
    scene,
    itemsFound,
    actions,
    content,
    loading: sceneLoading,
    error: sceneError,
  }
}

export default usePOI
